import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { useAuthStore } from '../../stores/authStore';
import { useAcombaBridgeStore } from '../../stores/acombaBridgeStore';
import { useIsAcomba } from '../../hooks/useIsAcomba';
import { getTimeOfDay, getFirstName, TimeOfDay } from '../../utils/greeting';

/**
 * "Bonjour Gabriel," / "Bon après-midi," — the time-of-day salutation shown as Home's page title.
 */
export function useSalutation(): string {
    const { t } = useTranslation();
    const displayName = useAuthStore(s => s.displayName);
    const user = useAuthStore(s => s.user);
    const isAcomba = useIsAcomba();
    const bridgeUserName = useAcombaBridgeStore(s => s.sessionInfo?.userName ?? null);

    // An Acomba desktop session has no AcceoConnect, so `displayName` (from /connect/me) is never
    // filled there. The connected Acomba user is the one Acomba itself pushed to the bridge at launch,
    // and it comes back on every bridge health poll — unlike `user.name` (OIDC profile), which is
    // in-memory only and therefore absent after a reload and on a silent session-reuse relaunch.
    // Read only for desktop: an AcceoConnect session must never be named after a bridge company's user.
    const nameSource = displayName ?? (isAcomba ? bridgeUserName : null) ?? user?.name;
    const firstName = getFirstName(nameSource);
    const timeOfDay = getTimeOfDay();

    const salutationKey =
        timeOfDay === TimeOfDay.Morning   ? TranslationKey.Home.Greeting.Morning :
        timeOfDay === TimeOfDay.Afternoon ? TranslationKey.Home.Greeting.Afternoon :
                                            TranslationKey.Home.Greeting.Evening;

    // Space injected into {{name}} so "Bonjour Gabriel," works without trailing space when name is absent
    return t(salutationKey, { name: firstName ? ` ${firstName}` : '' });
}
